import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import {
  FileSignature, Download, CheckCircle2, RefreshCw, XCircle,
  Clock as ClockIcon, FileText,
} from 'lucide-react';

/**
 * HR-side review surface for a candidate's signed offer letter.
 * Visible on EmployeeDetail.jsx when the employee is in/after the BGC stage.
 */
export default function SignedOfferReviewSection({ employeeId, canManage, onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null); // 'approve' | 'reupload' | 'reject' | null
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get(`/employees/${employeeId}/signed-offer`);
      setState(data);
    } catch (e) {
      // 'not_uploaded'/'locked' returns OK; only real errors land here
      console.debug('[signed-offer load]', e);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const downloadSigned = async () => {
    if (!state?.file_id) return;
    try {
      const resp = await api.get(`/employees/${employeeId}/signed-offer/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Download failed'); }
  };

  const downloadOriginal = async () => {
    try {
      const resp = await api.get(`/employees/${employeeId}/offer-letter.pdf?inline=true`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Download failed'); }
  };

  const submitDecision = async () => {
    if ((action === 'reupload' || action === 'reject') && !note.trim()) {
      toast.error('Please share a brief reason'); return;
    }
    setBusy(true);
    try {
      await api.post(`/employees/${employeeId}/signed-offer/decision`, { action, note: note || null });
      toast.success({
        approve: 'Signed offer approved',
        reupload: 'Candidate asked to re-upload',
        reject: 'Signed offer rejected',
      }[action]);
      setAction(null); setNote('');
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!state || state.status === 'locked' || state.status === 'not_uploaded') {
    // Don't render anything noisy on profiles that have no upload yet.
    if (!canManage) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="signed-offer-review">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <FileSignature className="w-3.5 h-3.5" /> Signed offer letter
        </div>
        <div className="text-sm text-slate-500 mt-2">
          {state?.status === 'not_uploaded'
            ? 'Awaiting the candidate to sign and upload the offer letter PDF.'
            : 'Signed offer upload becomes available after BGC is cleared.'}
        </div>
      </div>
    );
  }

  const statusPill = {
    pending_review:    { label: 'Pending HR review', cls: 'bg-amber-100 text-amber-800',   icon: <ClockIcon className="w-3.5 h-3.5" /> },
    approved:          { label: 'Approved',          cls: 'bg-emerald-100 text-emerald-800', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
    reupload_required: { label: 'Re-upload required', cls: 'bg-orange-100 text-orange-800', icon: <RefreshCw className="w-3.5 h-3.5" /> },
    rejected:          { label: 'Rejected',          cls: 'bg-red-100 text-red-800',       icon: <XCircle className="w-3.5 h-3.5" /> },
  }[state.status] || { label: state.status, cls: 'bg-slate-100 text-slate-700' };

  const canDecide = canManage && state.status === 'pending_review';

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="signed-offer-review">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            <FileSignature className="w-3.5 h-3.5" /> Signed offer letter
          </div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">{state.filename || 'Signed offer letter'}</h2>
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${statusPill.cls}`} data-testid={`signed-offer-status-${state.status}`}>
              {statusPill.icon} {statusPill.label}
            </span>
            {state.uploaded_at && <span>· Uploaded {state.uploaded_at.slice(0, 16).replace('T', ' ')}</span>}
            {state.size && <span>· {(state.size / 1024).toFixed(0)} KB</span>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadOriginal}
                  className="rounded-full" data-testid="download-original-offer">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Original PDF
          </Button>
          <Button variant="outline" size="sm" onClick={downloadSigned}
                  className="rounded-full" data-testid="download-signed-offer">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Signed PDF
          </Button>
        </div>
      </div>

      {state.review_note && state.status !== 'pending_review' && (
        <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="review-note-display">
          <b>Review note:</b> {state.review_note}
          {state.reviewed_by_name && <span className="text-slate-500"> · by {state.reviewed_by_name}{state.reviewed_at ? ` on ${state.reviewed_at.slice(0, 10)}` : ''}</span>}
        </div>
      )}

      {canDecide && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={() => { setAction('approve'); setNote(''); }} data-testid="approve-signed-offer"
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
          </Button>
          <Button onClick={() => { setAction('reupload'); setNote(''); }} variant="outline" data-testid="reupload-signed-offer"
                  className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-50">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Ask for re-upload
          </Button>
          <Button onClick={() => { setAction('reject'); setNote(''); }} variant="outline" data-testid="reject-signed-offer"
                  className="rounded-full border-red-300 text-red-700 hover:bg-red-50">
            <XCircle className="w-4 h-4 mr-1.5" /> Reject
          </Button>
        </div>
      )}

      {state.history && state.history.length > 0 && (
        <details className="text-xs text-slate-500" data-testid="signed-offer-review-history">
          <summary className="cursor-pointer hover:text-slate-700">Review history ({state.history.length})</summary>
          <ul className="mt-2 space-y-1.5 ml-4">
            {state.history.map((h, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-3">
                <b className="text-slate-700">{h.action}</b>
                {' · '}<span>{h.at?.slice(0, 16).replace('T', ' ')}</span>
                {h.by_name && <> · {h.by_name}</>}
                {h.note && <div className="italic">Note: {h.note}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Decision dialog */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setNote(''); } }}>
        <DialogContent className="max-w-md" data-testid="signed-offer-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {action === 'approve' && 'Approve signed offer letter'}
              {action === 'reupload' && 'Ask candidate to re-upload'}
              {action === 'reject' && 'Reject signed offer letter'}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600">
            {action === 'approve' && 'The candidate will be notified that their signed offer letter is approved.'}
            {action === 'reupload' && 'The candidate will be asked to upload a new signed PDF. Please share what needs to be corrected.'}
            {action === 'reject' && 'This rejects the candidate\u2019s signed offer letter. They will receive an email with the reason.'}
          </div>
          {action !== 'approve' && (
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-600">Reason {action !== 'approve' && <span className="text-red-500">*</span>}</label>
              <Textarea data-testid="decision-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder={action === 'reupload' ? 'e.g. signature missing on page 2, illegible scan…' : 'e.g. mismatched signature, fraudulent document…'}
                        className="rounded-xl mt-1.5" />
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAction(null)} disabled={busy} className="rounded-full">Cancel</Button>
            <Button onClick={submitDecision} disabled={busy} data-testid="confirm-decision"
                    className={`rounded-full text-white ${
                      action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700'
                      : action === 'reupload' ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-red-600 hover:bg-red-700'
                    }`}>
              {busy ? 'Submitting…' : (action === 'approve' ? 'Approve' : action === 'reupload' ? 'Send request' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
